import { Formik, Form } from 'formik';
import {
   Box,
   Button,
   CircularProgress,
   Divider,
   FormHelperText,
   Paper,
   Stack,
   TextField,
   Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link } from 'react-router-dom';
import ModuleCard from './ModuleCard';
import StyledDashboardLayout from '../style';

export const RolePermissionForm = ({
   initialValues,
   handleSubmit,
   validationSchema,
   showData,
   isEdit,
}) => {
   return (
      <Formik
         initialValues={initialValues}
         validationSchema={validationSchema}
         onSubmit={handleSubmit}
         enableReinitialize
      >
         {({
            values,
            errors,
            touched,
            setFieldValue,
            handleBlur,
            handleSubmit: submitForm,
            isSubmitting,
         }) => {
            const toggleModule = (module) => {
               const exists = values.modules.find((m) => m.moduleId === module.moduleId);

               if (exists) {
                  setFieldValue(
                     'modules',
                     values.modules.filter((m) => m.moduleId !== module.moduleId),
                  );
                  return;
               }

               const readPermission = module.permissions.find((p) =>
                  p.name?.toLowerCase().includes('read'),
               );

               setFieldValue('modules', [
                  ...values.modules,
                  {
                     moduleId: module.moduleId,
                     permissionIds: readPermission ? [readPermission.id] : [],
                  },
               ]);
            };

            const togglePermission = (moduleId, permissionId) => {
               const modules = [...values.modules];

               const moduleIndex = modules.findIndex((m) => m.moduleId === moduleId);

               if (moduleIndex === -1) return;

               const permissions = modules[moduleIndex].permissionIds;

               const currentModule = showData?.find((m) => m.moduleId === moduleId);
               const readPermission = currentModule?.permissions.find((p) =>
                  p.name?.toLowerCase().includes('read'),
               );

               if (permissions.includes(permissionId)) {
                  if (readPermission && permissionId === readPermission.id) {
                     modules[moduleIndex].permissionIds = [];
                  } else {
                     modules[moduleIndex].permissionIds = permissions.filter(
                        (id) => id !== permissionId,
                     );
                  }
               } else {
                  if (readPermission && !permissions.includes(readPermission.id)) {
                     modules[moduleIndex].permissionIds = [
                        ...permissions,
                        readPermission.id,
                        permissionId,
                     ];
                  } else {
                     modules[moduleIndex].permissionIds = [...permissions, permissionId];
                  }
               }

               setFieldValue('modules', modules);
            };

            return (
               <StyledDashboardLayout>
                  <div className="content-wrapper">
                     <div className="content-header">
                        <div className="container-fluid">
                           <div className="d-flex justify-content-between mb-2">
                              <div className="pageTitle">
                                 <h1 className="ml-5">{isEdit ? 'Edit Role' : 'Add Role'}</h1>
                              </div>
                              <div className="btnWrap">
                                 <Link to="/role-permissions" className="btn addBtn">
                                    <ArrowBackIcon /> Back
                                 </Link>
                              </div>
                           </div>
                        </div>
                     </div>

                     <div className="card customCard p-3 m-3">
                        <Paper elevation={0} sx={{ p: 3 }}>
                           <Typography
                              variant="h5"
                              component="h2"
                              gutterBottom
                              sx={{
                                 fontWeight: 600,
                                 color: '#333',
                                 mb: 3,
                                 display: 'flex',
                                 alignItems: 'center',
                              }}
                           >
                              {isEdit ? 'Update Role & Permissions' : 'Role & Permissions'}
                           </Typography>

                           <Divider sx={{ mb: 3 }} />

                           <Form onSubmit={submitForm}>
                              <Box className="card-body" sx={{ padding: '0' }}>
                                 <TextField
                                    fullWidth
                                    label="Role Name *"
                                    placeholder="Enter role name"
                                    value={values.roleName}
                                    onBlur={handleBlur}
                                    onChange={(e) => setFieldValue('roleName', e.target.value)}
                                    error={touched.roleName && Boolean(errors.roleName)}
                                    helperText={touched.roleName && errors.roleName}
                                    sx={{
                                       mb: 4,
                                       '& .MuiOutlinedInput-root': {
                                          '&:hover fieldset': { borderColor: '#3F30FF' },
                                          '&.Mui-focused fieldset': { borderColor: '#3F30FF' },
                                       },
                                       '& .MuiInputLabel-root': {
                                          '&.Mui-focused': { color: '#3F30FF' },
                                       },
                                    }}
                                 />

                                 <Typography fontWeight={600} mb={2}>
                                    Module Permissions
                                 </Typography>

                                 <Stack spacing={2}>
                                    {showData?.map((module) => {
                                       if (module?.moduleName === 'hour_rate') return <></>;

                                       const checked = values.modules.some(
                                          (m) => m.moduleId === module.moduleId,
                                       );

                                       const selectedModule = values.modules.find(
                                          (m) => m.moduleId === module.moduleId,
                                       );

                                       return (
                                          <ModuleCard
                                             key={module.moduleId}
                                             module={module}
                                             checked={checked}
                                             selectedModule={selectedModule}
                                             toggleModule={toggleModule}
                                             togglePermission={togglePermission}
                                          />
                                       );
                                    })}
                                 </Stack>

                                 {typeof errors.modules === 'string' && (
                                    <FormHelperText error sx={{ mt: 2 }}>
                                       {errors.modules}
                                    </FormHelperText>
                                 )}

                                 <Box
                                    sx={{
                                       display: 'flex',
                                       gap: 2,
                                       justifyContent: 'flex-end',
                                       mt: 5,
                                    }}
                                 >
                                    <Button
                                       type="button"
                                       variant="outlined"
                                       component={Link}
                                       to="/role-permissions"
                                       sx={{
                                          borderColor: '#6c757d',
                                          color: '#6c757d',
                                          '&:hover': {
                                             borderColor: '#545b62',
                                             backgroundColor: '#f8f9fa',
                                          },
                                       }}
                                    >
                                       Cancel
                                    </Button>

                                    <Button
                                       type="submit"
                                       variant="contained"
                                       disabled={isSubmitting}
                                       className="commonBtn"
                                       startIcon={
                                          isSubmitting ? (
                                             <CircularProgress size={18} color="inherit" />
                                          ) : null
                                       }
                                       sx={{
                                          backgroundColor: '#3F30FF',
                                          '&:hover': { backgroundColor: '#2f23cc' },
                                          minWidth: '120px',
                                       }}
                                    >
                                       {isEdit ? 'Update Role' : 'Save Role'}
                                    </Button>
                                 </Box>
                              </Box>
                           </Form>
                        </Paper>
                     </div>
                  </div>
               </StyledDashboardLayout>
            );
         }}
      </Formik>
   );
};

export default RolePermissionForm;
